/* eslint-disable no-unused-expressions */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { sendTrackingInfo } from '@panel/commons';
import styled from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import PerformanceGreen from '@panel/commons/assets/icons/performance-green.svg';
import PerformanceYellow from '@panel/commons/assets/icons/performance-yellow.svg';
import PerformanceRed from '@panel/commons/assets/icons/performance-red.svg';
import PerformanceGrey from '@panel/commons/assets/icons/performance-grey.svg';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';

const UpperInfoRight = ({ device, posting, linkToDesempeno, usedForTour, postingStatus }) => {
  const { t } = useTranslation();
  const { performanceStatus, daysLeftToQuality } = posting;

  const performanceDisabled = ['DRAFT', 'OFFLINE', 'WITHOUT_CREDIT'].includes(postingStatus);
  const statusDisabled = ['DRAFT', 'WITHOUT_CREDIT'].includes(postingStatus);
  const statusDisabledFiltered = statusDisabled ? `.${postingStatus}` : '';
  const statusFiltered = performanceDisabled || !performanceStatus ? 'NO-DATA-AVAILABLE' : performanceStatus;
  const showDaysForPerformance =
    statusFiltered === 'NO-DATA-AVAILABLE' &&
    typeof daysLeftToQuality === 'number' &&
    daysLeftToQuality !== 0 &&
    daysLeftToQuality > 0 &&
    daysLeftToQuality < 30;
  const showCalculatingText = showDaysForPerformance ? '.calculating' : null;

  const getButtonText = () => {
    if (performanceDisabled || (statusFiltered === 'NO-DATA-AVAILABLE' && daysLeftToQuality === 0)) {
      return '2';
    }
    if (statusFiltered === 'NO-DATA-AVAILABLE') {
      return '0';
    }
    if (statusFiltered === 'ALTO') {
      return '1';
    }
    return '2';
  };

  const tooltipComponent = (status, daysLeft, width) => (
    <TooltipComponent width={width}>
      {t(
        `panel.avisos.posting.quality.tooltip.${status}${
          typeof daysLeft === 'number' && daysLeft > 1 ? '.plural' : ''
        }`,
        {
          value: daysLeft
        }
      )}
    </TooltipComponent>
  );

  const calculateFillPercentage = daysLeft => {
    const maxDays = 29;
    if (typeof daysLeft !== 'number' || daysLeft <= 0 || daysLeft > maxDays) {
      return 0;
    }
    return ((maxDays - daysLeft) / maxDays) * 100;
  };

  return (
    <Container data-tour-panel={usedForTour && 'leads-info-1'} device={device}>
      <SimpleTooltip
        backgroundColor={select('black')}
        offset={5}
        isMobile={device === 'tablet' || !statusFiltered}
        position='bottom-center'
        text={tooltipComponent(statusFiltered.concat(statusDisabledFiltered), null, '270px')}
        dataPanel='infoTo-improvePerformance'
      >
        <PerformanceLabel status={statusFiltered}>
          <span />
          <div data-panel='avisos-performanceStatus'>{t(`panel.aviso.desempeno.status.${statusFiltered}`)}</div>
        </PerformanceLabel>
      </SimpleTooltip>

      <SimpleTooltip
        backgroundColor={select('black')}
        offset={5}
        isMobile={!showDaysForPerformance}
        position='bottom-center'
        text={tooltipComponent(statusFiltered.concat(showCalculatingText), daysLeftToQuality)}
        dataPanel='infoToCta-improvePerformance'
        width='169px'
        height='49px'
      >
        <PerformanceStatus
          data-panel='posting-performanceStatus'
          className={device === 'desktop' && 'DESKTOP-STATUS-TAG'}
          data-tour-panel={usedForTour && 'performance'}
          href={statusFiltered !== 'NO-DATA-AVAILABLE' ? linkToDesempeno : null}
          rel='noopener noreferrer'
          status={statusFiltered}
          onClick={() => {
            sendTrackingInfo(
              'Panel',
              'Mis avisos',
              `Card del aviso-${statusFiltered === 'ALTO' ? 'Ver desempeño' : 'Mejorar desempeño'}`
            );
          }}
        >
          <ContentWrapper
            status={statusFiltered}
            disabled={performanceDisabled || (statusFiltered === 'NO-DATA-AVAILABLE' && daysLeftToQuality === 0)}
          >
            <div className='buttonText'>{t(`panel.avisos.posting.CTA${getButtonText()}`)}</div>
            {showDaysForPerformance && <DaysLeftBar fillValue={calculateFillPercentage(daysLeftToQuality)} />}
          </ContentWrapper>
        </PerformanceStatus>
      </SimpleTooltip>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-height: 25px;
  display: flex;
  margin-bottom: 14px;
  justify-content: space-between;
  align-items: center;
`;

const PerformanceColors = {
  ALTO: { background: 'positiveBack', color: 'snackGreen', icon: PerformanceGreen, hoverBackground: 'midPositive' },
  MEDIO: { background: 'lightYellow', color: 'brownYellow', icon: PerformanceYellow, hoverBackground: 'midYellow' },
  BAJO: { background: 'redBack', color: 'negative', icon: PerformanceRed, hoverBackground: 'midNegative' },
  'NO-DATA-AVAILABLE': {
    background: 'lightGray',
    color: 'grayDarkLight',
    icon: PerformanceGrey,
    hoverBackground: 'lightGray'
  }
};

const PerformanceStatus = styled.a`
  width: 150px;
  min-height: 28px;
  border-radius: 5px;
  background-color: ${({ status }) => status && select(PerformanceColors[status].background)};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: ${({ status }) => (status === 'NO-DATA-AVAILABLE' ? 'not-allowed' : 'pointer')};
  overflow: hidden;
  &.DESKTOP-STATUS-TAG {
    width: 249px;
    box-shadow: ${({ status }) => status !== 'NO-DATA-AVAILABLE' && '0px 2px 5px rgba(0, 0, 0, 0.07)'};
    :hover {
      box-shadow: ${({ status }) => status !== 'NO-DATA-AVAILABLE' && '0px 2px 5px rgba(0, 0, 0, 0.1)'};
      background-color: ${({ status }) => status && select(PerformanceColors[status].hoverBackground)};
    }
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  .buttonText {
    font-family: Hind;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    color: ${({ status, disabled }) =>
      status && select(!disabled ? PerformanceColors[status].color : 'grayMediumLight')};
    z-index: 1;
  }
`;

const DaysLeftBar = styled.div`
  width: ${({ fillValue }) => `${fillValue}%` || '0%'};
  height: 100%;
  background-color: ${select('grayMedium')};
  position: absolute;
  left: 0;
  z-index: 0;
`;

const PerformanceLabel = styled.div`
  display: flex;
  align-items: center;
  > span {
    display: inline-block;
    background-image: ${({ status }) => status && `url(${PerformanceColors[status].icon})`};
    background-repeat: no-repeat;
    background-size: contain;
    width: 18px;
    height: 16px;
    margin-right: 6px;
  }
  > div {
    font-family: Hind;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 19px;
    color: ${({ status }) =>
      status && select(status === 'NO-DATA-AVAILABLE' ? 'darkGray' : PerformanceColors[status].color)};
  }
`;

const TooltipComponent = styled.div`
  width: ${({ width }) => width || 'auto'};
  white-space: pre-wrap;
  padding: 4px 0;
`;

export default UpperInfoRight;
